import { OTHER_SUBTYPES } from 'pages/ordering/categoryConfig'
import { useMemo } from 'react'
import type { ComponentTypesV2 } from 'types/selectComponent'
import useHardwareSelectorFetchEngine from './useHardwareSelectorFetchEngine'

interface GroupedHardwareData {
  modules: any[]
  inverters: any[]
  batteries: any[]
  mounting: any[]
  moduleLoading: boolean
  moduleLoaded: boolean
  inverterLoading: boolean
  inverterLoaded: boolean
  batteryLoading: boolean
  batteryLoaded: boolean
  mountingLoading: boolean
  mountingLoaded: boolean
  pageSize: number
}

const SHOP_CATALOG_PERSISTENT = { referrer: 'shop' as const }

const useGroupedHardwareSelector = (
  defaultFilterValues?: { [key: string]: any },
  options?: { listAbortSignal?: AbortSignal }
): GroupedHardwareData => {
  const { listAbortSignal } = options || {}

  const otherSubtypes = OTHER_SUBTYPES as ComponentTypesV2[]

  // Match historical behaviour: second-arg `distributor` was never passed from the shop home,
  // so ordering always used `_distributor_count`.
  const sortBy = '_distributor_count'
  const pageSize = 10

  // Separate fetch calls for each group
  const {
    componentsData: moduleData,
    loading: moduleLoading,
    loaded: moduleLoaded,
  } = useHardwareSelectorFetchEngine({
    componentTypes: ['module'],
    defaultFilterValues,
    limit: pageSize,
    sortBy,
    persistentFilterValuesOverride: SHOP_CATALOG_PERSISTENT,
    listAbortSignal,
  })

  const {
    componentsData: inverterData,
    loading: inverterLoading,
    loaded: inverterLoaded,
  } = useHardwareSelectorFetchEngine({
    componentTypes: ['inverter'],
    defaultFilterValues,
    limit: 10,
    sortBy,
    persistentFilterValuesOverride: SHOP_CATALOG_PERSISTENT,
    listAbortSignal,
  })

  const {
    componentsData: batteryData,
    loading: batteryLoading,
    loaded: batteryLoaded,
  } = useHardwareSelectorFetchEngine({
    componentTypes: ['battery'],
    defaultFilterValues,
    limit: pageSize,
    sortBy,
    persistentFilterValuesOverride: SHOP_CATALOG_PERSISTENT,
    listAbortSignal,
  })

  const {
    componentsData: mountingData,
    loading: mountingLoading,
    loaded: mountingLoaded,
  } = useHardwareSelectorFetchEngine({
    componentTypes: otherSubtypes,
    defaultFilterValues,
    limit: pageSize,
    sortBy,
    persistentFilterValuesOverride: SHOP_CATALOG_PERSISTENT,
    listAbortSignal,
  })

  const groupedData = useMemo(
    () => ({
      modules: moduleData || [],
      inverters: inverterData || [],
      batteries: batteryData || [],
      mounting: mountingData || [],
      moduleLoading,
      moduleLoaded,
      inverterLoading,
      inverterLoaded,
      batteryLoading,
      batteryLoaded,
      mountingLoading,
      mountingLoaded,
      pageSize,
    }),
    [
      moduleData,
      inverterData,
      batteryData,
      mountingData,
      moduleLoading,
      moduleLoaded,
      inverterLoading,
      inverterLoaded,
      batteryLoading,
      batteryLoaded,
      mountingLoading,
      mountingLoaded,
      pageSize,
    ]
  )

  return groupedData
}

export default useGroupedHardwareSelector
